import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, LogOut, Heart, Calendar, User, MapPin, Trash2, Shield } from "lucide-react";
import { Link } from "react-router-dom";

export default function Account() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user === false) {
      navigate("/login", { replace: true, state: { from: { pathname: "/account" } } });
    }
  }, [loading, user, navigate]);

  if (loading || user === null || user === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const initials = (user.name || user.email || "U")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background font-sans flex flex-col">
      <Navbar />
      <main className="flex-grow pt-28 pb-12 px-4">
        <div className="container mx-auto max-w-5xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
            <div className="flex items-center gap-5">
              <Avatar className="w-20 h-20 border-2 border-primary/10 shadow-sm">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="bg-primary text-white font-serif text-xl">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1
                  className="text-3xl md:text-4xl font-serif font-bold text-primary"
                  data-testid="account-greeting"
                >
                  Hello, <span className="text-secondary">{user.name || "Traveler"}</span>
                </h1>
                <p className="text-muted-foreground" data-testid="account-email">
                  {user.email}
                </p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {(user.providers || []).map((p) => (
                    <Badge key={p} variant="outline" className="border-primary/20 text-xs capitalize">
                      {p === "google" ? "Google" : "Email"}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="admin-link">
                  <Button
                    variant="outline"
                    className="rounded-full border-secondary/40 text-secondary hover:bg-secondary hover:text-white"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Button>
                </Link>
              )}
              <Button
                variant="outline"
                data-testid="logout-btn"
                className="rounded-full border-primary/20"
                onClick={async () => {
                  await logout();
                  toast.success("Signed out");
                  navigate("/");
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="bg-white border border-primary/10">
              <TabsTrigger value="profile" data-testid="tab-profile">
                <User className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="bookings" data-testid="tab-bookings">
                <Calendar className="w-4 h-4 mr-2" />
                My Bookings
              </TabsTrigger>
              <TabsTrigger value="favorites" data-testid="tab-favorites">
                <Heart className="w-4 h-4 mr-2" />
                Favorites
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <ProfileTab user={user} updateProfile={updateProfile} />
            </TabsContent>
            <TabsContent value="bookings">
              <BookingsTab />
            </TabsContent>
            <TabsContent value="favorites">
              <FavoritesTab />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ProfileTab({ user, updateProfile }) {
  const [name, setName] = useState(user.name || "");
  const [country, setCountry] = useState(user.country || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ name, country, phone });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-primary/10">
      <CardHeader>
        <CardTitle className="font-serif text-2xl text-primary">Profile</CardTitle>
        <CardDescription>Update your personal details</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4 max-w-lg">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={user.email} disabled className="bg-gray-50" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-name">Name</Label>
            <Input
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="profile-name-input"
              className="bg-white"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-country">Country</Label>
              <Input
                id="profile-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Sri Lanka"
                data-testid="profile-country-input"
                className="bg-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-phone">Phone</Label>
              <Input
                id="profile-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+94 7X XXX XXXX"
                data-testid="profile-phone-input"
                className="bg-white"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={saving}
            data-testid="profile-save-btn"
            className="bg-primary text-white hover:bg-primary/90 rounded-full"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    api
      .get("/bookings")
      .then(({ data }) => setBookings(data))
      .catch(() => setBookings([]));
  }, []);

  if (bookings === null) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <Card className="border-primary/10">
        <CardContent className="py-16 text-center">
          <Calendar className="w-12 h-12 mx-auto text-primary/40 mb-4" />
          <h3 className="font-serif text-xl text-primary mb-2">No bookings yet</h3>
          <p className="text-muted-foreground mb-6">
            Start planning your dream trip to Sri Lanka.
          </p>
          <Button
            className="bg-primary text-white hover:bg-primary/90 rounded-full"
            onClick={() => (window.location.href = "/booking")}
          >
            Plan a Trip
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="bookings-list">
      {bookings.map((b) => (
        <Card key={b.booking_id} className="border-primary/10">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-serif text-lg font-bold text-primary">
                    {b.first_name} {b.last_name}
                  </h3>
                  <Badge className="bg-secondary/20 text-secondary-foreground border-secondary/30 capitalize">
                    {b.status}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {b.interest || "—"} • {b.duration || "—"} • Travel:{" "}
                  {b.travel_date || "Not set"}
                </p>
                {b.message && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    "{b.message}"
                  </p>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Requested {new Date(b.created_at).toLocaleDateString()}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function FavoritesTab() {
  const [favs, setFavs] = useState(null);

  useEffect(() => {
    api
      .get("/favorites")
      .then(({ data }) => setFavs(data))
      .catch(() => setFavs([]));
  }, []);

  const remove = async (place_id) => {
    try {
      const { data } = await api.delete(`/favorites/${place_id}`);
      setFavs(data);
      toast.success("Removed from favorites");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (favs === null) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (favs.length === 0) {
    return (
      <Card className="border-primary/10">
        <CardContent className="py-16 text-center">
          <Heart className="w-12 h-12 mx-auto text-primary/40 mb-4" />
          <h3 className="font-serif text-xl text-primary mb-2">No favorites yet</h3>
          <p className="text-muted-foreground">
            Tap the heart on any destination to save it for later.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-list">
      {favs.map((f) => (
        <Card key={f.place_id} className="overflow-hidden border-primary/10">
          {f.image && (
            <div className="h-40 overflow-hidden">
              <img src={f.image} alt={f.name} className="w-full h-full object-cover" />
            </div>
          )}
          <CardContent className="p-4">
            <h3 className="font-serif text-lg font-bold text-primary">{f.name}</h3>
            {f.location && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" /> {f.location}
              </p>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 text-red-600 hover:bg-red-50"
              data-testid={`favorite-remove-${f.place_id}`}
              onClick={() => remove(f.place_id)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
